import { el } from "./dom";
import { icon } from "./icons";
import type { View } from "./list-state";

// The board (brief 1A-6, mockup .board): the same rows as the list, grouped into one column per
// stage with its count, each row a tile that opens its record. And the List/Board switch over
// them (mockup .vbar and .seg2); the list is the default.

export interface BoardDef<T> {
  /** The stage columns, in order. */
  stages: readonly string[];
  stageOf: (row: T) => string;
  /** The tile's lines: the first is bold, the last muted (mockup tile). */
  tile: (row: T) => (Node | string)[];
}

export function board<T>(
  def: BoardDef<T>,
  rows: readonly T[],
  rowId: (row: T) => string,
  onOpen?: (row: T) => void,
): HTMLElement {
  return el(
    "div",
    { class: "board" },
    ...def.stages.map((stage) => {
      const its = rows.filter((r) => def.stageOf(r) === stage);
      return el(
        "section",
        { class: "col", "aria-label": `${stage}: ${its.length}` },
        el("h3", {}, el("span", {}, stage), el("span", {}, String(its.length))),
        ...its.map((r) => {
          const lines = def.tile(r);
          const t = el(
            "button",
            { type: "button", class: "tile", "data-row": rowId(r) },
            ...lines.map((line, i) =>
              i === 0
                ? el("b", {}, line)
                : el(
                    "span",
                    { class: i === lines.length - 1 && i > 1 ? "muted" : undefined },
                    line,
                  ),
            ),
          );
          if (onOpen) t.addEventListener("click", () => onOpen(r));
          return t;
        }),
      );
    }),
  );
}

/** The bar over a list: how many rows, and the Board/List switch (mockup vtoggle). */
export function viewBar(count: string, view: View | null, onView: (v: View) => void): HTMLElement {
  const bar = el("div", { class: "vbar" }, el("span", { class: "muted small" }, count));
  if (view === null) return bar;
  const options: [View, string, string, string][] = [
    ["board", "project", "Board", "Board: columns by stage"],
    ["list", "list-unordered", "List", "List: a table with checkboxes for bulk actions"],
  ];
  bar.append(
    el(
      "div",
      { class: "seg2", role: "group", "aria-label": "View" },
      ...options.map(([v, ic, label, tip]) => {
        const b = el(
          "button",
          { type: "button", "aria-pressed": view === v ? "true" : "false", "data-tip": tip },
          icon(ic),
          ` ${label}`,
        );
        b.addEventListener("click", () => onView(v));
        return b;
      }),
    ),
  );
  return bar;
}
